import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Divider,
  Tooltip,
  message,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  IdcardOutlined,
  EditOutlined,
  LockOutlined,
  TeamOutlined,
  CrownOutlined,
  ReloadOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import { getMyProfileApi, changePasswordApi } from "../api/profile.api";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwForm] = Form.useForm();
  const { user } = useAuth();
  console.log(user);
  // ── Fetch profile ──────────────────────────────────────────────────────────
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await getMyProfileApi(user?.id);

      setProfile(data.data);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  // ── Change password ────────────────────────────────────────────────────────
  const handlePasswordChange = async (values) => {
    if (values.new_password !== values.confirm_password) {
      pwForm.setFields([
        { name: "confirm_password", errors: ["Passwords do not match"] },
      ]);
      return;
    }
    setPwLoading(true);
    try {
      const result = await changePasswordApi(user?.id, {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      console.log(result);
      message.success("Password updated successfully!");
      setPwModalOpen(false);
      pwForm.resetFields();
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        pwForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] })),
        );
      } else {
        message.error(
          err.response?.data?.message || "Failed to update password.",
        );
      }
    } finally {
      setPwLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  const formatDateTime = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";
  console.log(profile);
  const emp = profile?.employee || {};
  const userData = profile?.user || {};
console.log(emp, userData);
  // Avatar initials
  const initials = emp.full_name
    ? emp.full_name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  return (
    <div>
      {/* ── Page Header ── */}
      <Row
        align="middle"
        justify="space-between"
        style={{ marginBottom: 20, padding: "10px 10px", margin: 20 }}
      >
        <Col>
          <Space align="center">
            <UserOutlined style={{ fontSize: 22, color: "black" }} />
            <Title level={4} style={{ color: "black", margin: 0 }}>
              My Profile
            </Title>
            <Tag color="blue" style={{ borderRadius: 20 }}>
              Employee
            </Tag>
          </Space>
        </Col>
        <Col>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchProfile}
              loading={loading}
              style={styles.ghostBtn}
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={20} align="top">
        {/* ── LEFT: Avatar & Summary ── */}
        <Col xs={24} md={7}>
          <Card
            loading={loading}
            style={{ ...styles.card, textAlign: "center" }}
            bodyStyle={{ padding: "28px 20px" }}
          >
            {/* Avatar */}
            <div style={styles.avatarWrap}>
              <div style={styles.avatar}>{initials}</div>
              {emp.is_active && <div style={styles.avatarDot} />}
            </div>

            <Title level={5} style={{ margin: "0 0 4px", color: "black" }}>
              {emp.full_name || "—"}
            </Title>
            <Text
              style={{
                fontSize: 12,
                color: "#6b6880",
                display: "block",
                marginBottom: 14,
              }}
            >
              {emp.job_title || "—"}
            </Text>

            {/* Tags */}
            <Space wrap style={{ justifyContent: "center", marginBottom: 20 }}>
              <Tag
                color={emp.is_active ? "success" : "default"}
                style={{ borderRadius: 20 }}
              >
                {emp.is_active ? "Active" : "Inactive"}
              </Tag>
              {emp.department && (
                <Tag color="purple" style={{ borderRadius: 20 }}>
                  {emp.department}
                </Tag>
              )}
              {userData.role && (
                <Tag
                  color={userData.role === "admin" ? "volcano" : "geekblue"}
                  style={{ borderRadius: 20, textTransform: "capitalize" }}
                  icon={
                    userData.role === "admin" ? (
                      <CrownOutlined />
                    ) : (
                      <UserOutlined />
                    )
                  }
                >
                  {userData.role}
                </Tag>
              )}
            </Space>

            <Divider style={{ margin: "0 0 16px" }} />

            {/* Quick meta */}
            <div style={{ textAlign: "left" }}>
              {[
                { icon: <MailOutlined />, value: emp.email },
                { icon: <PhoneOutlined />, value: emp.phone },
                {
                  icon: <CalendarOutlined />,
                  value:
                    formatDate(emp.hire_date) &&
                    `Hired ${formatDate(emp.hire_date)}`,
                },
                { icon: <NumberOutlined />, value: emp.employee_code },
              ].map((row, i) => (
                <div key={i} style={styles.metaRow}>
                  <span style={styles.metaIcon}>{row.icon}</span>
                  <Text style={{ fontSize: 12 }}>{row.value || "—"}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* ── RIGHT: Detail Cards ── */}
        <Col xs={24} md={17}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {/* Employee Information */}
            <Card
              loading={loading}
              style={styles.card}
              bodyStyle={{ padding: 20 }}
              title={
                <Space>
                  <IdcardOutlined style={{ color: "black" }} />
                  <Text strong style={{ color: "black" }}>
                    Employee Information
                  </Text>
                </Space>
              }
              headStyle={styles.cardHead}
            >
              <SectionLabel>Identity</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol label="Employee ID" value={emp.id} mono />
                  <FieldCol
                    label="Employee Code"
                    value={emp.employee_code}
                    mono
                  />
                  <FieldCol label="Full Name" value={emp.full_name} />
                  <FieldCol label="User ID (linked)" value={emp.user_id} mono />
                </Row>
              </div>

              <SectionLabel style={{ marginTop: 16 }}>Contact</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol label="Email" value={emp.email} />
                  <FieldCol label="Phone" value={emp.phone} />
                </Row>
              </div>

              <SectionLabel style={{ marginTop: 16 }}>Position</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol label="Department" value={emp.department} />
                  <FieldCol label="Job Title" value={emp.job_title} />
                  <FieldCol
                    label="Hire Date"
                    value={formatDate(emp.hire_date)}
                  />
                  <Col xs={12} sm={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Status
                    </Text>
                    <Tag
                      color={emp.is_active ? "success" : "default"}
                      style={{ borderRadius: 20 }}
                    >
                      {emp.is_active ? "Active" : "Inactive"}
                    </Tag>
                  </Col>
                </Row>
              </div>

              <SectionLabel style={{ marginTop: 16 }}>Timestamps</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol
                    label="Created At"
                    value={formatDateTime(emp.created_at)}
                    mono
                    muted
                  />
                  <FieldCol
                    label="Updated At"
                    value={formatDateTime(emp.updated_at)}
                    mono
                    muted
                  />
                </Row>
              </div>
            </Card>

            {/* Account Information */}
            <Card
              loading={loading}
              style={styles.card}
              bodyStyle={{ padding: 20 }}
              title={
                <Space>
                  <TeamOutlined style={{ color: "black" }} />
                  <Text strong style={{ color: "black" }}>
                    Account Information
                  </Text>
                </Space>
              }
              headStyle={styles.cardHead}
            >
              <SectionLabel>User Record</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol label="User ID" value={userData.id} mono />
                  <FieldCol label="Name" value={userData.name} />
                  <FieldCol label="Email" value={userData.email} />
                  <Col xs={12} sm={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Role
                    </Text>
                    <Tag
                      color={userData.role === "admin" ? "volcano" : "geekblue"}
                      icon={
                        userData.role === "admin" ? (
                          <CrownOutlined />
                        ) : (
                          <UserOutlined />
                        )
                      }
                      style={{ borderRadius: 20, textTransform: "capitalize" }}
                    >
                      {userData.role || "—"}
                    </Tag>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Account Status
                    </Text>
                    <Tag
                      color={userData.is_active ? "success" : "default"}
                      style={{ borderRadius: 20 }}
                    >
                      {userData.is_active ? "Active" : "Disabled"}
                    </Tag>
                  </Col>
                </Row>
              </div>

              <SectionLabel style={{ marginTop: 16 }}>Security</SectionLabel>
              <div style={styles.passwordRow}>
                <Space>
                  <div style={styles.passwordIcon}>
                    <LockOutlined style={{ color: "rgb(120,57,120)" }} />
                  </div>
                  <div>
                    <Text strong style={{ fontSize: 13, display: "block" }}>
                      Password
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ••••••••••••
                    </Text>
                  </div>
                </Space>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setPwModalOpen(true)}
                  style={styles.primaryBtn}
                >
                  Change Password
                </Button>
              </div>

              <SectionLabel style={{ marginTop: 16 }}>Timestamps</SectionLabel>
              <div style={styles.insetBox}>
                <Row gutter={[16, 14]}>
                  <FieldCol
                    label="Created At"
                    value={formatDateTime(userData.created_at)}
                    mono
                    muted
                  />
                  <FieldCol
                    label="Updated At"
                    value={formatDateTime(userData.updated_at)}
                    mono
                    muted
                  />
                </Row>
              </div>
            </Card>
          </Space>
        </Col>
      </Row>

      {/* ── Change Password Modal ── */}
      <Modal
        title={
          <Text strong style={{ color: "black", fontSize: 16 }}>
            <EditOutlined style={{ color: "black", marginRight: 8 }} />
            Change Password
          </Text>
        }
        open={pwModalOpen}
        onCancel={() => {
          setPwModalOpen(false);
          pwForm.resetFields();
        }}
        footer={null}
        styles={{
          content: { background: "#ffffff", borderRadius: 12 },
          header: { background: "#ffffff", borderBottom: "1px solid #334155" },
          mask: { backdropFilter: "blur(4px)" },
        }}
        width={440}
      >
        <Form
          form={pwForm}
          layout="vertical"
          onFinish={handlePasswordChange}
          requiredMark={false}
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Current Password</Text>}
            name="current_password"
            rules={[
              { required: true, message: "Current password is required" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Enter current password"
              style={styles.modalInput}
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>New Password</Text>}
            name="new_password"
            rules={[
              { required: true, message: "New password is required" },
              { min: 8, message: "Minimum 8 characters" },
              {
                pattern: /[A-Z]/,
                message: "Must contain at least one uppercase letter",
              },
              { pattern: /[0-9]/, message: "Must contain at least one number" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Min 8 chars, 1 uppercase, 1 number"
              style={styles.modalInput}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            label={
              <Text style={{ color: "#94a3b8" }}>Confirm New Password</Text>
            }
            name="confirm_password"
            rules={[
              { required: true, message: "Please confirm your new password" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Re-enter new password"
              style={styles.modalInput}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setPwModalOpen(false);
                  pwForm.resetFields();
                }}
                style={styles.ghostBtn}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={pwLoading}
                style={styles.primaryBtn}
              >
                Update Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ── Small helper components ───────────────────────────────────────────────────

const SectionLabel = ({ children, style }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "1px",
      textTransform: "uppercase",
      color: "#94a3b8",
      marginBottom: 10,
      ...style,
    }}
  >
    {children}
  </div>
);

const FieldCol = ({ label, value, mono, muted }) => (
  <Col xs={12} sm={6}>
    <Text
      type="secondary"
      style={{ fontSize: 11, display: "block", marginBottom: 4 }}
    >
      {label}
    </Text>
    <Text
      style={{
        fontSize: mono ? 12 : 13,
        fontWeight: muted ? 400 : 500,
        color: muted ? "#94a3b8" : "black",
        fontFamily: mono ? "'DM Mono', monospace" : "inherit",
        wordBreak: "break-all",
      }}
    >
      {value ?? "—"}
    </Text>
  </Col>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  card: {
    background: "white",
    border: "1px solid #e2e0e7",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    background: "white",
    borderBottom: "1px solid #e2e0e7",
  },
  insetBox: {
    background: "#f8f7fa",
    border: "1px solid #e2e0e7",
    borderRadius: 8,
    padding: "14px 16px",
  },
  passwordRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "#f8f7fa",
    border: "1px solid #e2e0e7",
    borderRadius: 8,
    flexWrap: "wrap",
    gap: 10,
  },
  passwordIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "rgba(120,57,120,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarWrap: {
    position: "relative",
    display: "inline-block",
    marginBottom: 16,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgb(120,57,120), #8b5cf6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    fontWeight: 600,
    color: "#fff",
    margin: "0 auto",
  },
  avatarDot: {
    position: "absolute",
    bottom: 3,
    right: 3,
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "#16a34a",
    border: "2px solid white",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 0",
    color: "#6b6880",
  },
  metaIcon: { color: "rgb(120,57,120)", width: 16, textAlign: "center" },
  modalInput: {
    background: "white",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "black",
  },
  primaryBtn: {
    background: "rgb(120, 57, 120)",
    border: "none",
    borderRadius: 8,
  },
  ghostBtn: {
    background: "transparent",
    border: "1px solid #334155",
    color: "#94a3b8",
    borderRadius: 8,
  },
};

export default Profile;
